import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import * as AccountActions from 'src/app/store/account/account.actions';
import { selectActiveUsers, selectAllUsers, selectIntegrationSchoolSyncError, selectIntegrationSchoolSyncLoading, selectIntegrationSchoolSyncResult, selectIntegrationSyncStartedDatetime, selectSyncModeError, selectSyncModeSaving, selectDeactivatingSchool, selectDeactivateSchoolError, selectUser, selectSchoolInfo, selectSchoolError, selectSchoolLoading, selectUserError, selectUserLoading } from "./account.selectors";
import { Observable } from "rxjs";
import { switchMap } from "rxjs/operators";
import { SyncIntegrationResponse } from "@core/models/response/account.response";
import { selectUserAccountId } from "../auth/auth.selectors";
import { School, SchoolUser, SyncMode } from "@core/models/domain/account.domain";
import { AddUserRequest, ModifyUserRequest, ModifyUserProfileRequest, ModifySchoolRequest, UpdateSchoolContactRequest, UpdateSchoolLogoRequest } from "@core/models/request/account.request";
import { AccountService } from "@core/services/account.service";
import { PersistencyService } from "@core/services/persistency.service";
import { AppState } from "../app.reducer";

@Injectable({ providedIn: 'root' })
export class AccountStoreFacade {
    users$: Observable<SchoolUser[]> = this.store.select(selectAllUsers);
    // Memoized list view: non-INACTIVE users with only their ACTIVE groups.
    activeUsers$: Observable<SchoolUser[]> = this.store.select(selectActiveUsers);
    userError$: Observable<string | null> = this.store.select(selectUserError);
    userLoading$: Observable<boolean> = this.store.select(selectUserLoading);

    schoolInfo$: Observable<School> = this.store.select(selectSchoolInfo);
    integrationSchoolSyncResult$: Observable<SyncIntegrationResponse | null> = this.store.select(selectIntegrationSchoolSyncResult);
    integrationSchoolSyncLoading$: Observable<boolean> = this.store.select(selectIntegrationSchoolSyncLoading);
    integrationSchoolSyncError$: Observable<string | null> = this.store.select(selectIntegrationSchoolSyncError);
    integrationSyncStartedDatetime$: Observable<string | null> = this.store.select(selectIntegrationSyncStartedDatetime);
    syncModeSaving$: Observable<boolean> = this.store.select(selectSyncModeSaving);
    syncModeError$: Observable<string | null> = this.store.select(selectSyncModeError);
    schoolError$: Observable<string | null> = this.store.select(selectSchoolError);
    schoolLoading$: Observable<boolean> = this.store.select(selectSchoolLoading);
    deactivatingSchool$: Observable<boolean> = this.store.select(selectDeactivatingSchool);
    deactivateSchoolError$: Observable<string | null> = this.store.select(selectDeactivateSchoolError);

    constructor(
        private store: Store<AppState>,
        private persistencyService: PersistencyService,
        private accountService: AccountService
    ) {}

    loadUsers(): void {
        this.store.dispatch(AccountActions.loadUsers());
    }

    // Store-first: renders the cached roster instantly and only fetches when the
    // cache is empty, stale or invalidated.
    loadUsersIfNeeded(): void {
        this.store.dispatch(AccountActions.loadUsersIfNeeded());
    }

    loadSchoolInfo(schoolAccountId: string): void {
        this.store.dispatch(AccountActions.loadSchoolInfo({ schoolAccountId }));
    }

    loadSchoolInfoFromIntegration(schoolId: string): Observable<School | undefined> {
        return this.accountService.loadSchoolInfoFromIntegration(schoolId);
    }

    /** {@code confirmLargeRemovals}: Sync anyway, after a run stopped because tasks would lose too many pupils. */
    syncSchoolDataFromIntegration(schoolId: string, schoolAccountId: string, confirmLargeRemovals = false, syncMode?: SyncMode): void {
        this.store.dispatch(AccountActions.syncSchoolDataFromIntegration({
            schoolId,
            schoolAccountId,
            ...(confirmLargeRemovals && { confirmLargeRemovals }),
            ...(syncMode && { syncMode })
        }));
    }

    setSyncMode(schoolAccountId: string, syncMode: SyncMode): void {
        this.store.dispatch(AccountActions.setSyncMode({ schoolAccountId, syncMode }));
    }

    /** Asks the backend where the school's synchronization stands; polling continues from the effect. */
    loadSyncStatus(schoolAccountId: string): void {
        this.store.dispatch(AccountActions.loadSyncStatus({ schoolAccountId }));
    }

    updateSchool(request: ModifySchoolRequest): void {
        this.store.dispatch(AccountActions.updateSchool({ request }));
    }

    /** Takes the school off LOQT (DIGI-1855); success signs the admin out. */
    deactivateSchool(confirmSchoolName: string): void {
        this.store.dispatch(AccountActions.deactivateSchool({ confirmSchoolName }));
    }

    /** Forgets an earlier deactivation refusal, so the Settings page opens without it. */
    clearDeactivateSchoolError(): void {
        this.store.dispatch(AccountActions.clearDeactivateSchoolError());
    }

    updateSchoolLogo(request: UpdateSchoolLogoRequest): void {
        this.store.dispatch(AccountActions.updateSchoolLogo({ request }));
    }

    updateSchoolContact(request: UpdateSchoolContactRequest): void {
        this.store.dispatch(AccountActions.updateSchoolContact({ request }));
    }

    addUser(request: AddUserRequest): void {
        this.store.dispatch(AccountActions.addUser({ request }));
    }

    updateUser(request: ModifyUserRequest): void {
        this.store.dispatch(AccountActions.updateUser({ request }));
    }

    removeUser(userAccountId: string): void {
        this.store.dispatch(AccountActions.removeUser({ userAccountId }));
    }

    updateUserProfile(request: ModifyUserProfileRequest): void {
        this.store.dispatch(AccountActions.updateUserProfile({ request }));
    }

    getUser(userAccountId: string): Observable<SchoolUser | undefined> {
        return this.store.select(selectUser(userAccountId));
    }

    getCurrentUser(): Observable<SchoolUser | undefined> {
        // Account ID comes from the in-memory auth store (source of truth), not storage.
        return this.store.select(selectUserAccountId).pipe(
            switchMap((userAccountId) => this.getUser(userAccountId ?? ''))
        );
    }

    // Direct one-shot fetch (get-user/{accountId}), bypassing the store. Used by the
    // header so the avatar renders without waiting for the full get-users roster.
    fetchUser(userAccountId: string): Observable<SchoolUser | undefined> {
        return this.accountService.loadUser(userAccountId);
    }
}